import { getAuthToken, refreshAuthToken, clearAuthSession } from '@nohara-dxdev/react-oidc';
import { configureApiClient } from '@nohara-dxdev/oidc-core';
import { AxiosInstance } from 'axios';
import { getI18n } from 'react-i18next';
import { toast } from 'sonner';

import { env } from '@/config/env';

export const api: AxiosInstance = configureApiClient({
  baseURL: `${env.API_ENDPOINT}/v1`,
  getAuthToken: getAuthToken,
  refreshAuthToken: refreshAuthToken,
  onUnauthorized: clearAuthSession,
});

// Abort a request that hasn't answered in 10s instead of leaving the caller
// hanging on a stalled connection. Retries are handled by react-query (reads
// only) — see the QueryClient defaults in `app/provider.tsx`.
api.defaults.timeout = 10_000;

/**
 * Timeout for the handful of endpoints that do real work in-request — Excel
 * parsing, validation, calculation. A large BIM workbook can run for minutes,
 * so these call sites pass `{ timeout: LONG_RUNNING_TIMEOUT_MS }` instead of
 * the 10s default.
 *
 * This only stops the *client* giving up early; it cannot extend how long the
 * server is allowed to take. API Gateway caps an HTTP API integration at 30s,
 * hard, and that is what ends these requests — the calculation and export route
 * Lambdas are given 2 minutes in `backend/api/v1/*.properties`, so the gateway
 * always cuts first. A request that genuinely needs minutes fails upstream well
 * before this fires. Making big files actually work means moving calculation off
 * the request path (job + polling), not raising this number.
 *
 * 600s is deliberately far above every server-side ceiling: it exists so the
 * browser does not abandon a legitimately long upload, and it can never cause a
 * re-send, because the server always answers first (and mutations are not
 * retried — see the QueryClient defaults in `app/provider.tsx`).
 */
export const LONG_RUNNING_TIMEOUT_MS = 600_000;

// Tell the backend which org the request is for. A multi-org user's token
// carries several org scopes, so the backend can't infer the acting org from
// the token alone — it takes it from this header (validated against the token)
// and every request is scoped to the org in the current URL (`/o/:orgId`).
// Matched as a UUID so sibling routes like `/o/onboarding` and `/o/profile`
// (which have no org yet) don't send a bogus org.
api.interceptors.request.use((config) => {
  const orgId = window.location.pathname.match(/\/o\/([0-9a-f-]{36})(?:\/|$)/i)?.[1];
  if (orgId) config.headers.set('X-Org-Id', orgId);
  return config;
});

// Add 403 response interceptor for forbidden errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 403) {
      // `@nohara-dxdev/react-core` used to expose a module-level `notifications`
      // object, but it's hook-only now (`useNotifications`). Interceptors run
      // outside React, so we talk to sonner's `toast` directly here.
      toast.error(getI18n().t('common.errors.forbidden'));
    }
    return Promise.reject(error);
  }
);
